import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { ContactFormProps, ContactFormValues } from ".";
import { useStyle } from "./style";
import {
	Field,
	Button,
	Text,
	tokens,
	Divider,
	Checkbox,
	CheckboxOnChangeData,
	mergeClasses,
} from "@fluentui/react-components";
import { CaretDown16Filled, CaretRight16Filled } from "@fluentui/react-icons";
import { useIntl } from "react-intl";
import { Flex, GapSize } from "@/components/atoms/flex";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RegEx } from "@/common/constants";
import { QueuesState, useQueuesStore } from "@/common/stores/nonpersistingStores/queues";
import { ContactDto, Queue } from "@/repository/models";
import {
	CheckedState,
	MultiCheckbox,
	MultiCheckboxItemProps,
} from "@/components/molecules/multiCheckbox";
import { FormTextField } from "@/components/molecules/formTextField/FormTextField";
import { useFormTextFieldStyle } from "@/components/molecules/formTextField/style";

export function ContactForm(props: ContactFormProps) {
	const classes = useStyle();
	const formTextFieldClasses = useFormTextFieldStyle();

	const intl = useIntl();

	const [selectedQueueIds, setSelectedQueueIds] = useState(
		props.contact?.queueIds || []
	);

	const [areExtraFieldsVisible, setAreExtraFieldsVisible] =
		useState<boolean>(false);

	const [allQueuesChecked, setAllQueuesChecked] = useState<boolean>(false);

	const agentQueues = useQueuesStore((state: QueuesState) => state.queues);

	const formMethods = useForm<ContactFormValues>({
		mode: "all",
		reValidateMode: "onChange",
		defaultValues: {
			...props.contact,
			queueIds: props.contact?.queueIds || [],
		},
	});

	const {
		formState: { errors, isValid },
		trigger,
		getValues,
		setValue,
		register,
		unregister,
		reset,
	} = formMethods;

	useEffect(() => {
		reset({
			...props.contact,
			queueIds: props.contact?.queueIds || [],
		});
		setSelectedQueueIds(props.contact?.queueIds || []);
	}, [props.contact]);

	const onSubmit: SubmitHandler<ContactFormValues> = (
		contactFormValues: ContactFormValues
	) => {
		if (props.isSaveInProgress) {
			return;
		}

		const contactDto: ContactDto = {
			...contactFormValues,
			mobilePhoneNumber: contactFormValues.mobilePhone,
			businessPhoneNumber: contactFormValues.businessPhone,
		};
		props.saveContact(contactDto);
	};

	const handleSaveClick = (_: any) => {
		trigger();
		if (isValid) {
			const contactFormValues = getValues();
			onSubmit(contactFormValues);
		}
	};

	const handleQueueCheckboxChange = useCallback(
		(item: MultiCheckboxItemProps, checked: CheckedState) => {
			if (checked) {
				setSelectedQueueIds([...selectedQueueIds, item.key]);
			} else {
				let newArray = selectedQueueIds.filter((x) => x !== item.key);
				setSelectedQueueIds(newArray);
			}
		},
		[agentQueues, selectedQueueIds]
	);

	const agentQueueIds = useMemo<string[]>(() => {
		return !!agentQueues ? agentQueues.map((x: Queue) => x.id) : [];
	}, [agentQueues]);

	useEffect(() => {
		if (!props.queueSelectionEnabled) {
			return;
		}

		const agentQueuesNotSelected = agentQueueIds.filter(
			(x) => !selectedQueueIds.includes(x)
		);
		setAllQueuesChecked(agentQueuesNotSelected.length == 0);

		const queueIds = getValues().queueIds;
		if (!(queueIds && queueIds.length == 0 && selectedQueueIds.length == 0)) {
			setValue("queueIds", selectedQueueIds, {
				shouldValidate: true,
			});
		}
	}, [agentQueueIds, selectedQueueIds, props.queueSelectionEnabled]);

	const contactQueues = useMemo<MultiCheckboxItemProps[]>(() => {
		return !!agentQueues
			? agentQueues.map((x: Queue) => ({
					key: x.id,
					label: x.name,
					checked: selectedQueueIds?.includes(x.id) || false,
				}))
			: [];
	}, [agentQueues, selectedQueueIds]);

	useEffect(() => {
		if (props.queueSelectionEnabled) {
			register("queueIds", {
				validate: (value) => {
					return (
						(value && value.length > 0) ||
						intl.formatMessage({
							id: "Contacts.Form.NoQueueSelected",
						})
					);
				},
			});
		} else {
			unregister("queueIds");
		}
	}, [props.queueSelectionEnabled]);

	const validateGroup = () => {
		const values = getValues();
		return (
			Boolean(values.businessPhone || values.mobilePhone || values.email) ||
			intl.formatMessage({
				id: "Contacts.Form.OneOfTheFieldsIsRequired",
			})
		);
	};

	const getFormTextFieldsClassesNames = (propertyName: string) => {
		return props.highlightedFields?.includes(propertyName)
			? { inputField: classes.highlightedInputField }
			: {};
	};

	return (
		<>
			<FormProvider {...formMethods}>
				<form>
					<Flex gap={GapSize.ExtraLarge} hAlign="space-between">
						<div className={classes.fieldsContainer}>
							<Flex gap={GapSize.Large} hAlign="space-between">
								<FormTextField
									classesNames={getFormTextFieldsClassesNames("name")}
									propertyName="name"
									translationKey="Contacts.Form.Name"
									value={props.contact?.name}
									maxLength={50}
									validationRules={{
										required: {
											value: true,
											message: intl.formatMessage({
												id: "Common.Validations.RequiredField",
											}),
										},
									}}
								/>
								<FormTextField
									classesNames={getFormTextFieldsClassesNames("email")}
									propertyName="email"
									translationKey="Contacts.Form.Email"
									value={props.contact?.email}
									pattern={RegEx.Email}
									maxLength={50}
									validationRules={{
										validate: validateGroup,
									}}
									hideCustomValidationError={true}
									afterChange={() => {
										trigger(["mobilePhone", "businessPhone"]);
									}}
								/>
							</Flex>
							<Flex gap={GapSize.Large}>
								<FormTextField
									classesNames={getFormTextFieldsClassesNames("mobilePhone")}
									propertyName="mobilePhone"
									translationKey="Contacts.Form.MobilePhoneNumber"
									value={props.contact?.mobilePhone}
									pattern={RegEx.PhoneNumber}
									maxLength={32}
									validationRules={{
										validate: validateGroup,
									}}
									hideCustomValidationError={true}
									afterChange={() => {
										trigger(["mobilePhone", "email"]);
									}}
								/>
								<FormTextField
									classesNames={getFormTextFieldsClassesNames("businessPhone")}
									propertyName="businessPhone"
									translationKey="Contacts.Form.BusinessPhoneNumber"
									value={props.contact?.businessPhone}
									pattern={RegEx.PhoneNumber}
									maxLength={32}
									validationRules={{
										validate: validateGroup,
									}}
									hideCustomValidationError={true}
									afterChange={() => {
										trigger(["email", "businessPhone"]);
									}}
								/>
							</Flex>
							{errors.email &&
								errors.email.type == "validate" &&
								errors.mobilePhone && (
									<Flex column>
										<Text className={classes.groupedFieldsValidationMessage}>
											{errors?.email.message}
										</Text>
									</Flex>
								)}
							<Flex>
								<Button
									appearance="transparent"
									className={classes.extraFieldsButton}
									icon={
										areExtraFieldsVisible ? (
											<CaretDown16Filled
												color={tokens.colorNeutralBackground6}
											/>
										) : (
											<CaretRight16Filled
												color={tokens.colorNeutralBackground3Pressed}
											/>
										)
									}
									onClick={() => {
										setAreExtraFieldsVisible((oldState) => !oldState);
									}}
								>
									<Text size={200}>
										{intl.formatMessage({ id: "Contacts.Form.MoreFields" })}
									</Text>
								</Button>
							</Flex>
							{areExtraFieldsVisible && (
								<>
									<Flex gap={GapSize.Large}>
										<FormTextField
											propertyName="givenName"
											translationKey="Contacts.Form.GivenName"
											value={props.contact?.givenName}
											maxLength={256}
										/>
										<FormTextField
											propertyName="surname"
											translationKey="Contacts.Form.Surname"
											value={props.contact?.surname}
											maxLength={256}
										/>
									</Flex>
									<Flex gap={GapSize.Large}>
										<FormTextField
											propertyName="userPrincipalName"
											translationKey="Contacts.Form.Sip"
											value={props.contact?.userPrincipalName}
											pattern={RegEx.Sip}
											maxLength={50}
										/>
									</Flex>
									<Flex gap={GapSize.Large}>
										<FormTextField
											propertyName="jobTitle"
											translationKey="Contacts.Form.JobTitle"
											value={props.contact?.jobTitle}
											maxLength={50}
										/>
										<FormTextField
											propertyName="department"
											translationKey="Contacts.Form.Department"
											value={props.contact?.department}
											maxLength={50}
										/>
									</Flex>
									<Flex gap={GapSize.Large}>
										<FormTextField
											propertyName="city"
											translationKey="Contacts.Form.City"
											value={props.contact?.city}
											maxLength={50}
										/>
										<FormTextField
											propertyName="companyName"
											translationKey="Contacts.Form.CompanyName"
											value={props.contact?.companyName}
											maxLength={256}
										/>
									</Flex>
									<Flex gap={GapSize.Large}>
										<FormTextField
											propertyName="office"
											translationKey="Contacts.Form.Office"
											value={props.contact?.office}
											maxLength={50}
										/>
										<FormTextField
											propertyName="country"
											translationKey="Contacts.Form.Country"
											value={props.contact?.country}
											maxLength={50}
										/>
									</Flex>
								</>
							)}
						</div>
						<div className={classes.queueListContainer}>
							{props.queueSelectionEnabled && (
								<Flex column>
									<Field
										label={intl.formatMessage({ id: "Contacts.Form.Queues" })}
										className={formTextFieldClasses.field}
										required
									></Field>
									<Divider className={classes.queuesDivider} />
									<Checkbox
										checked={allQueuesChecked}
										label={intl.formatMessage({ id: "Common.All" })}
										key={"-1"}
										onChange={(_any, data: CheckboxOnChangeData) => {
											setSelectedQueueIds(!!data.checked ? agentQueueIds : []);
											setAllQueuesChecked(!!data.checked);
										}}
									/>
									<Divider />
									<MultiCheckbox
										items={contactQueues}
										onChange={handleQueueCheckboxChange}
										classNames={{
											root: mergeClasses(
												classes.multiCheckbox,
												areExtraFieldsVisible
													? classes.multiCheckboxWhenFormExpanded
													: classes.multiCheckboxWhenFormCollapsed
											),
											item: classes.multiCheckboxItem,
										}}
									/>
									{errors?.queueIds && (
										<Text className={classes.queuesValidationMessage}>
											{errors?.queueIds.message}
										</Text>
									)}
								</Flex>
							)}
						</div>
					</Flex>
				</form>
			</FormProvider>
			<Flex
				hAlign="flex-end"
				gap={GapSize.Medium}
				className={classes.buttonsContainer}
			>
				<Button onClick={props.closeDialog}>
					{intl.formatMessage({ id: "Common.Cancel" })}
				</Button>
				<Button
					disabled={props.isSaveInProgress}
					type="submit"
					appearance="primary"
					onClick={handleSaveClick}
				>
					{props.saveLabel || intl.formatMessage({ id: "Common.Save" })}
				</Button>
			</Flex>
		</>
	);
}
